/**
 * Recovery for Next.js chunk-loading failures.
 *
 * A ChunkLoadError means a lazily-imported bundle never arrived — almost always
 * a transient network timeout, or a client left open across a deploy whose
 * chunk hashes have since changed. Neither is recoverable by re-rendering, so
 * `reset()` on an error boundary just re-throws. A full reload re-requests the
 * document and picks up the current chunk manifest, which does fix it.
 *
 * Reloading on an error is a loop risk, so the time of the last reload is kept
 * in sessionStorage and a second failure inside the cooldown is left to surface
 * normally. A timestamp (rather than a flag) needs no "clear on success" step,
 * which would otherwise run at bootstrap and defeat the guard.
 */

const RELOAD_AT_KEY = 'gp:chunk-reload-at'
export const RELOAD_COOLDOWN_MS = 30_000

export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const { name, message } = error as { name?: unknown; message?: unknown }
  if (name === 'ChunkLoadError') return true
  return typeof message === 'string' && /Loading (?:CSS )?chunk [\w-]+ failed/i.test(message)
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

/**
 * Claims the reload slot if none was taken within the cooldown. Pure apart
 * from the storage it is handed, so the guard is unit-testable.
 */
export function claimReloadSlot(storage: StorageLike, now: number): boolean {
  const last = Number(storage.getItem(RELOAD_AT_KEY))
  if (last && now - last < RELOAD_COOLDOWN_MS) return false
  storage.setItem(RELOAD_AT_KEY, String(now))
  return true
}

/**
 * Reloads for a chunk error unless one was already attempted recently.
 * Returns true when a reload was triggered, so callers can skip rendering a
 * fallback that is about to be torn down anyway.
 */
export function recoverFromChunkError(error: unknown): boolean {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) return false

  let claimed: boolean
  try {
    claimed = claimReloadSlot(window.sessionStorage, Date.now())
  } catch {
    // Private mode or blocked storage: without a guard a reload could loop,
    // so let the error surface instead.
    return false
  }
  if (!claimed) return false

  window.location.reload()
  return true
}
